import { useEffect, useMemo, useState } from "react";
import {
  Breadcrumbs,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import AppDialog from "../common/AppDialog";
import type {
  FileItem as FileItemType,
  FolderType,
} from "../../types/interfaces/filesStorage";
import { useGetFileSystemQuery } from "features/fileSystem/filesStorage";

interface MoveFileDialogProps {
  open: boolean;
  onClose: () => void;
  file: FileItemType | null;
  scopePrefix: string;
  moveInProgress: boolean;
  onMoveHere: (destinationPrefix: string) => Promise<void>;
}

export default function MoveFileDialog({
  open,
  onClose,
  file,
  scopePrefix,
  moveInProgress,
  onMoveHere,
}: MoveFileDialogProps) {
  const [browsePrefix, setBrowsePrefix] = useState(scopePrefix);

  useEffect(() => {
    if (!open) return;
    setBrowsePrefix(scopePrefix);
  }, [open, scopePrefix]);

  const fileSystemQueryArgs = useMemo(() => {
    const prefix = browsePrefix;
    return {
      prefix,
      continuation_token: null,
      search: null,
      limit: 200,
      folder_type: "all" as FolderType,
      page: 1,
      sort_field: "name" as const,
      sort_order: "asc" as const,
    };
  }, [browsePrefix]);

  const {
    data: fileSystemData,
    isLoading: fsLoading,
    isFetching: fsFetching,
    error: fsError,
  } = useGetFileSystemQuery(fileSystemQueryArgs, {
    skip: !open || !browsePrefix,
  });

  const rootQueryArgs = useMemo(
    () => ({
      prefix: "",
      continuation_token: null,
      search: null,
      limit: 200,
      folder_type: "all" as FolderType,
      page: 1,
      sort_field: "name" as const,
      sort_order: "asc" as const,
    }),
    [],
  );
  const { data: rootData } = useGetFileSystemQuery(rootQueryArgs, { skip: !open });

  const folders = useMemo(
    () => (fileSystemData?.folders ?? []).filter((i) => i.ftype === "folder"),
    [fileSystemData?.folders],
  );

  const scopeRootPrefix = useMemo(() => {
    const rootFolders = (rootData?.folders ?? []).filter((i) => i.ftype === "folder");
    const candidates = rootFolders
      .map((item) => item.fsource)
      .filter((p) => scopePrefix === p || scopePrefix.startsWith(`${p}/`))
      .sort((a, b) => b.length - a.length);
    return candidates[0] || scopePrefix;
  }, [rootData?.folders, scopePrefix]);

  const sourceParentPrefix = useMemo(() => {
    if (!file?.fsource) return "";
    const idx = file.fsource.lastIndexOf("/");
    if (idx <= 0) return "";
    return file.fsource.slice(0, idx);
  }, [file?.fsource]);

  const isCurrentFolder = sourceParentPrefix !== "" && browsePrefix === sourceParentPrefix;
  const isBusy = fsLoading || fsFetching;

  const browseRelativeParts = useMemo(() => {
    const root = (scopeRootPrefix || "").replace(/\/+$/, "");
    const current = (browsePrefix || "").replace(/\/+$/, "");
    if (!root || !current) return [];
    if (current === root) return [];
    if (current.startsWith(`${root}/`)) {
      return current.slice(root.length + 1).split("/").filter(Boolean);
    }
    return current.split("/").filter(Boolean);
  }, [browsePrefix, scopeRootPrefix]);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Move file"
      maxWidth="sm"
      actions={
        <>
          <Button onClick={onClose} disabled={moveInProgress}>
            Cancel
          </Button>
          <Button
            onClick={() => void onMoveHere(browsePrefix)}
            variant="contained"
            disabled={!file || isBusy || moveInProgress || isCurrentFolder}
          >
            {moveInProgress ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Move here"
            )}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          Source: <b>{file?.fname}</b>
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Breadcrumbs
            aria-label="move-file-breadcrumbs"
            separator="/"
            sx={{
              flex: 1,
              minWidth: 0,
              "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap", overflow: "hidden" },
              "& .MuiBreadcrumbs-li": { minWidth: 0 },
            }}
          >
            <Link
              underline="hover"
              color={browsePrefix === scopeRootPrefix ? "text.primary" : "inherit"}
              onClick={(e) => {
                e.preventDefault();
                setBrowsePrefix(scopeRootPrefix);
              }}
              sx={{ cursor: "pointer", fontSize: "0.85rem", whiteSpace: "nowrap" }}
            >
              Root
            </Link>
            {browseRelativeParts.map((seg, idx, arr) => {
              const isLast = idx === arr.length - 1;
              const prefixSuffix = arr.slice(0, idx + 1).join("/");
              const targetPrefix = `${scopeRootPrefix}/${prefixSuffix}`;
              return (
                <Link
                  key={`${targetPrefix}-${idx}`}
                  underline="hover"
                  color={isLast ? "text.primary" : "inherit"}
                  onClick={(e) => {
                    e.preventDefault();
                    if (isLast) return;
                    setBrowsePrefix(targetPrefix);
                  }}
                  sx={{
                    cursor: isLast ? "default" : "pointer",
                    fontSize: "0.85rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 180,
                  }}
                >
                  {seg}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
          Destination: <b>{browsePrefix}</b>
        </Typography>
        {isCurrentFolder ? (
          <Box sx={{ mt: -0.8 }}>
            <Chip
              size="small"
              label="Current folder (You are here)"
              color="default"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        ) : null}

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1,
          }}
        >
          {isBusy ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : folders.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              No subfolders under this location.
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {folders.map((item) => (
                (() => {
                  const isSourceFolder = item.fsource === sourceParentPrefix;
                  return (
                <ListItemButton
                  key={`${item.fsource}-${item.fname}`}
                  onClick={() => {
                    setBrowsePrefix(item.fsource);
                  }}
                  disabled={moveInProgress}
                  selected={isSourceFolder}
                  sx={{
                    borderRadius: 1,
                    ...(isSourceFolder
                      ? {
                          bgcolor: "action.selected",
                          "&:hover": { bgcolor: "action.selected" },
                        }
                      : {}),
                  }}
                >
                  <ListItemText
                    primary={item.fname}
                    secondary={
                      isSourceFolder ? "You are here" : undefined
                    }
                  />
                </ListItemButton>
                  );
                })()
              ))}
            </List>
          )}
        </Box>

        {fsError ? (
          <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
            Failed to load destination folders.
          </Typography>
        ) : null}
      </Box>
    </AppDialog>
  );
}
